/**
 * Test Data Uploader Utility
 * テスト用のドキュメントデータを一括アップロードするユーティリティ
 */

import { documentService } from '../services/document.service';

export interface TestFileInfo {
  path: string;
  name: string;
  category: string;
  tags: string[];
  description: string;
}

export interface BatchUploadOptions {
  category?: string;
  tags?: string[];
  department?: string;
  classification?: 'public' | 'internal' | 'confidential';
  onProgress?: (progress: number, currentFile: string) => void;
  onError?: (error: Error, file: string) => void;
  onSuccess?: (file: string) => void;
}

/**
 * インシデントファイルのメタデータを生成
 */
function generateIncidentMetadata(fileName: string): {
  tags: string[];
  description: string;
  title: string;
} {
  // ファイル名からプレフィックスを解析 (例: CH_N_P.pdf)
  const parts = fileName.replace('.pdf', '').split('_');
  const prefix = parts[0] || '';
  const type = parts[1] || '';

  // プレフィックスに基づいてシステム名を判定
  const systemNames: Record<string, string> = {
    'CH': 'チャンネル管理',
    'CL': 'クライアント',
    'CS': 'カスタマーサービス',
    'GH': 'GitHub連携',
    'GP': 'グループ管理',
    'IC': 'インシデント管理',
    'OP': 'オペレーション',
    'SP': 'サービスプロバイダー',
    'TS': 'テクニカルサポート',
    'WP': 'ワークプレース',
  };

  // タイプに基づいて優先度を判定
  const priorities: Record<string, string> = {
    'N': '通常',
    'S': '重要',
    'T': '緊急',
  };

  const systemName = systemNames[prefix] || 'その他';
  const priority = priorities[type] || '通常';

  return {
    title: `${systemName} - ${priority}優先度インシデント`,
    tags: [
      'インシデント',
      systemName,
      `優先度${priority}`,
      '過去トラ',
      '対応済み',
      new Date().getFullYear().toString(),
    ],
    description: `${systemName}システムで発生した${priority}優先度のインシデント記録。問題の詳細、原因分析、実施した対応、および再発防止策が記載されています。`,
  };
}

/**
 * 単一ファイルをアップロード
 */
async function uploadSingleFile(
  file: File,
  metadata: any,
  onProgress?: (progress: number) => void
): Promise<{ success: boolean; error?: Error }> {
  try {
    const response = await documentService.uploadDocument(
      file,
      metadata,
      onProgress
    );

    // アップロードステータスを確認
    let retryCount = 0;
    const maxRetries = 30;

    while (retryCount < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 1000));

      try {
        const status = await documentService.getUploadStatus(response.id);

        if (status.status === 'completed') {
          return { success: true };
        } else if (status.status === 'failed') {
          throw new Error(status.message || 'Processing failed');
        }
      } catch (error) {
        console.error('Error checking upload status:', error);
      }

      retryCount++;
    }

    throw new Error('Processing timeout');
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error('Unknown error')
    };
  }
}

/**
 * テストデータを一括アップロード
 */
export async function batchUploadTestData(
  files: File[],
  options: BatchUploadOptions = {}
): Promise<{
  succeeded: string[];
  failed: { file: string; error: Error }[];
}> {
  const {
    category = 'incident',
    tags = [],
    department = 'Operations',
    classification = 'internal',
    onProgress,
    onError,
    onSuccess,
  } = options;

  const succeeded: string[] = [];
  const failed: { file: string; error: Error }[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const progress = ((i + 1) / files.length) * 100;

    if (onProgress) {
      onProgress(progress, file.name);
    }

    // インシデントファイルの場合は自動でメタデータを生成
    const isIncident = category === 'incident';
    const generatedMetadata = isIncident
      ? generateIncidentMetadata(file.name)
      : { title: file.name, tags: [], description: '' };

    const metadata = {
      title: generatedMetadata.title,
      description: generatedMetadata.description,
      category,
      tags: [...new Set([...tags, ...generatedMetadata.tags])],
      department,
      classification,
      permissions: {
        defaultAccess: 'organization' as const,
        allowShare: true,
        allowDownload: true,
      },
    };

    const result = await uploadSingleFile(
      file,
      metadata,
      (fileProgress) => {
        const totalProgress = (i / files.length) * 100 + (fileProgress / files.length);
        if (onProgress) {
          onProgress(totalProgress, file.name);
        }
      }
    );

    if (result.success) {
      succeeded.push(file.name);
      if (onSuccess) {
        onSuccess(file.name);
      }
    } else {
      const error = result.error || new Error('Upload failed');
      failed.push({ file: file.name, error });
      if (onError) {
        onError(error, file.name);
      }
    }

    // レート制限を避けるため少し待機
    await new Promise(resolve => setTimeout(resolve, 500));
  }

  return { succeeded, failed };
}

/**
 * ローカルディレクトリからファイルを読み込んでアップロード
 * （ブラウザでは直接ファイルシステムにアクセスできないため、ファイル選択ダイアログを使用）
 */
export function createFileUploadInput(
  onFilesSelected: (files: File[]) => void,
  accept: string = '.pdf,.docx,.xlsx'
): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'file';
  input.multiple = true;
  input.accept = accept;

  input.addEventListener('change', (event) => {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const files = Array.from(target.files);
      onFilesSelected(files);
    }
  });

  return input;
}

/**
 * インシデントファイル用のクイックアップロード
 */
export async function quickUploadIncidentFiles(files: File[]): Promise<void> {
  console.log(`Starting batch upload of ${files.length} incident files...`);

  const result = await batchUploadTestData(files, {
    category: 'incident',
    tags: ['過去トラ', 'インシデント', '対応済み'],
    department: 'Operations',
    classification: 'internal',
    onProgress: (progress, fileName) => {
      console.log(`Progress: ${progress.toFixed(1)}% - ${fileName}`);
    },
    onError: (error, fileName) => {
      console.error(`Failed to upload ${fileName}:`, error.message);
    },
    onSuccess: (fileName) => {
      console.log(`✓ Successfully uploaded: ${fileName}`);
    },
  });

  console.log('\n=== Upload Summary ===');
  console.log(`Succeeded: ${result.succeeded.length} files`);
  console.log(`Failed: ${result.failed.length} files`);

  if (result.failed.length > 0) {
    console.log('\nFailed files:');
    result.failed.forEach(({ file, error }) => {
      console.log(`  - ${file}: ${error.message}`);
    });
  }
}
